import { useTranslation } from 'react-i18next';
import {
  FileTextOutlined,
  EditOutlined,
  CalendarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Spin, Empty, Card, Statistic, List, Avatar } from 'antd';
import { formatDate, DATE_FORMATS } from '@/utils/formUtils';
import { EMPTY_DISPLAY } from '@/shared/history';
import { getUserFullName } from '@/utils/utils';

/**
 * StatisticsTab
 *
 * Summary of a record's history: how many changes, when they started and
 * stopped, and who made most of them.
 */
export default function StatisticsTab({ data, loading, canViewSnapshots }) {
  const { t } = useTranslation('history');

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return <Empty description={t('ui.noStatistics')} className="my-8" />;
  }

  const {
    totalSnapshots,
    totalChanges,
    firstModification,
    lastModification,
    mostActiveUsers,
  } = data;

  const asDate = (value) =>
    value ? formatDate(value, DATE_FORMATS.DISPLAY_DOTS) : EMPTY_DISPLAY;

  return (
    <div className="p-4">
      <div className="mb-6 flex flex-col gap-4">
        {canViewSnapshots && (
          <Card>
            <Statistic
              title={t('ui.totalSnapshots')}
              value={totalSnapshots}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#3f8600', fontSize: '20px' }}
            />
          </Card>
        )}

        <Card>
          <Statistic
            title={t('ui.totalChanges')}
            value={totalChanges}
            prefix={<EditOutlined />}
            valueStyle={{ color: '#1890ff', fontSize: '20px' }}
          />
        </Card>

        <Card>
          <Statistic
            title={t('ui.firstModification')}
            value={asDate(firstModification)}
            prefix={<CalendarOutlined />}
            valueStyle={{ fontSize: '14px' }}
          />
        </Card>

        <Card>
          <Statistic
            title={t('ui.lastModification')}
            value={asDate(lastModification)}
            prefix={<CalendarOutlined />}
            valueStyle={{ fontSize: '14px' }}
          />
        </Card>
      </div>

      {mostActiveUsers && mostActiveUsers.length > 0 && (
        <Card title={t('ui.mostActiveUsers')} className="mt-4">
          <List
            dataSource={mostActiveUsers}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={
                    item.user
                      ? getUserFullName(item.user.firstName, item.user.lastName)
                      : t('ui.system')
                  }
                  description={
                    <span className="flex items-center gap-1">
                      <EditOutlined style={{ color: '#1890ff' }} />
                      <span className="font-medium">
                        {t('ui.changeCount', { count: item.count })}
                      </span>
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );
}
